import { all, call, put, select, takeLeading } from 'redux-saga/effects';
import { PromiseType } from 'utility-types';

import { buildPageRootFromDocumentParticipantFields } from '@app/libs/participantFields/buildRenderTree';
import workspaceService from '@app/libs/workspaceService';
import {
  CONTRACT_SETUP__DOCUMENT_AUTHENTICATIONS_FETCH_REQUEST,
  CONTRACT_SETUP__DOCUMENT_FIELDS_FETCH_REQUEST,
  CONTRACT_SETUP__DOCUMENT_FIELDS_FETCH_SUCCESS,
  CONTRACT_SETUP__DOCUMENT_PARTICIPANTS_FETCH_REQUEST,
  CONTRACT_SETUP_FIELD_REMOVE,
} from '@app/store/actionTypes';
import {
  contractSetupFieldRemove,
  fetchDocumentAuthentications,
  fetchDocumentAuthenticationsFailure,
  fetchDocumentAuthenticationsSuccess,
  fetchDocumentFields,
  fetchDocumentFieldsFailure,
  fetchDocumentFieldsSuccess,
  fetchDocumentParticipants,
  fetchDocumentParticipantsFailure,
  fetchDocumentParticipantsSuccess,
} from '@app/store/contract-setup/actions';
import ParticipantAuthenticationDomain from '@app/store/contract-setup/ParticipantAuthentication';
import RequesterField from '@app/store/contract-setup/RequesterField';
import { getParticipantsIds } from '@app/store/contract-setup/selectors';
import {
  removeNodeFromDocumentRenderTree,
  setDocumentRenderTree,
} from '@app/store/document/renderTree/actions';
import DocumentsParticipant from '@app/store/documents/Participant';
import { getDocumentsByIdDeprecated } from '@app/store/transport-layer/document/documents';
import { getDocumentsAttachmentsDeprecated } from '@app/store/transport-layer/document/documents/attachments';
import {
  getDocumentsParticipantAuths,
  ParticipantAuthentication,
} from '@app/store/transport-layer/document/documents/participant-auths';
import { getRequesterFields } from '@app/store/transport-layer/document/documents/requester-inputs';

export function* removeField({ payload: { uuid } }: ReturnType<typeof contractSetupFieldRemove>) {
  yield put(removeNodeFromDocumentRenderTree(uuid));
}

export function* fetchDocumentAuthenticationsSaga({
  payload: { documentId },
}: ReturnType<typeof fetchDocumentAuthentications>) {
  try {
    const participantsIds: ReturnType<typeof getParticipantsIds> = yield select(getParticipantsIds);
    const workspaceId = workspaceService.getWorkspaceId();
    const participantAuthenticationsResponse: ParticipantAuthentication[] = yield call(
      getDocumentsParticipantAuths,
      {
        documentId,
        workspaceId,
      }
    );

    /**
     * 수정 후 다시보내기에서는 보안 상 이유로 접근 암호는 복구안함
     */
    const participantAuthentications: ParticipantAuthenticationDomain[] =
      participantAuthenticationsResponse
        .map((participantAuthentication) => {
          const transformedParticipantAuthentication: ParticipantAuthenticationDomain = {
            participantId: participantAuthentication.participantId,
          };

          participantAuthentication.auths.forEach((auth) => {
            const splitedAuthValue = auth.authValue?.split('|');
            if (auth.authType === 'D_CERT' && splitedAuthValue && splitedAuthValue.length === 2) {
              transformedParticipantAuthentication.digitalCertificate = {
                bizNumber: splitedAuthValue[1],
                companyName: splitedAuthValue[0],
              };
            }
            if (
              auth.authType === 'MOBILE_IDENTIFICATION' &&
              splitedAuthValue &&
              splitedAuthValue.length === 2
            ) {
              transformedParticipantAuthentication.mobile = {
                phoneNumber: splitedAuthValue[1],
                realName: splitedAuthValue[0],
              };
            }
          });

          return transformedParticipantAuthentication;
        })
        .filter((participantAuthentication) =>
          participantsIds.includes(participantAuthentication.participantId || '')
        );

    yield put(
      fetchDocumentAuthenticationsSuccess({
        participantAuthentications,
      })
    );
  } catch (error) {
    yield put(fetchDocumentAuthenticationsFailure(error));
  }
}

export function* fetchDocumentFieldsSaga({
  payload: { documentId },
}: ReturnType<typeof fetchDocumentFields>) {
  try {
    const workspaceId = workspaceService.getWorkspaceId();
    const participantsIds: ReturnType<typeof getParticipantsIds> = yield select(getParticipantsIds);
    const { requesterFields }: { requesterFields: RequesterField[] } = yield call(
      getRequesterFields,
      {
        documentId,
        workspaceId,
      }
    );
    const {
      attachments: participantAttachments,
    }: PromiseType<ReturnType<typeof getDocumentsAttachmentsDeprecated>> = yield call(
      getDocumentsAttachmentsDeprecated,
      {
        documentId,
        workspaceId,
      }
    );

    yield put(
      fetchDocumentFieldsSuccess({
        participantFields: [],
        requesterFields,
        participantAttachments: participantAttachments.filter((participantAttachment) =>
          participantsIds.includes(participantAttachment.participant.participantId)
        ),
      })
    );
  } catch (error) {
    yield put(fetchDocumentFieldsFailure(error));
  }
}

export function* fetchDocumentFieldSucceededSaga({
  payload: { participantFields },
}: ReturnType<typeof fetchDocumentFieldsSuccess>) {
  const pageRoot = buildPageRootFromDocumentParticipantFields(participantFields);
  yield put(setDocumentRenderTree(pageRoot));
}

export function* fetchDocumentParticipantsSaga({
  payload: { documentId },
}: ReturnType<typeof fetchDocumentParticipants>) {
  try {
    const { participants }: { participants: DocumentsParticipant[] } = yield call(
      getDocumentsByIdDeprecated,
      {
        workspaceId: workspaceService.getWorkspaceId(),
        documentId,
      }
    );
    yield put(fetchDocumentParticipantsSuccess({ participants }));
  } catch (error) {
    yield put(fetchDocumentParticipantsFailure(error));
  }
}

export default function* contractSetupSagas() {
  yield all([
    takeLeading(CONTRACT_SETUP_FIELD_REMOVE, removeField),
    takeLeading(
      CONTRACT_SETUP__DOCUMENT_AUTHENTICATIONS_FETCH_REQUEST,
      fetchDocumentAuthenticationsSaga
    ),
    takeLeading(CONTRACT_SETUP__DOCUMENT_FIELDS_FETCH_REQUEST, fetchDocumentFieldsSaga),
    takeLeading(CONTRACT_SETUP__DOCUMENT_FIELDS_FETCH_SUCCESS, fetchDocumentFieldSucceededSaga),
    takeLeading(CONTRACT_SETUP__DOCUMENT_PARTICIPANTS_FETCH_REQUEST, fetchDocumentParticipantsSaga),
  ]);
}
